/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext,
    IRequestContext
} from '@msdyn365-commerce/core';
import { buildCacheKey, generateImageUrl, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';

import { IPageSummaryData } from '@msdyn365-commerce-modules/page-summary';
import { ILobasArticlePageSummaryConfig } from './lobas-article-page-summary.props.autogenerated';
import { IArticle } from '../../actions/DataServiceEntities.g';
import { getArticleByURLAsync } from '../../actions/DataActionExtension.g';

/**
 * Product Page Summary Input.
 */
export class ArticlePageSummaryInput implements IActionInput {
    public apiSettings: ICommerceApiSettings;

    public config: ILobasArticlePageSummaryConfig;

    public articleId: string;

    public constructor(
        config: ILobasArticlePageSummaryConfig,
        apiSettings: ICommerceApiSettings,
        articleId: string,
        requestContext?: IRequestContext
    ) {
        this.config = config || {};
        this.apiSettings = apiSettings;
        this.articleId = articleId;
    }

    public getCacheObjectType = (): string => 'ArticlePageSummary';

    public getCacheKey = (): string => `${buildCacheKey('ArticlePageSummary', this.apiSettings)}-${this.config.title}-${this.articleId}`;

    public dataCacheType = (): CacheType => 'application';
}

export interface IArticlePageSummary extends IPageSummaryData {
    articleId?: string;
}

const createInput = (args: ICreateActionContext) => {
    const urlParts = args.requestContext.url.requestUrl.href.split('/');
    const articleId = urlParts[urlParts.length - 1].split('?')[0];

    if (articleId) {
        return new ArticlePageSummaryInput(
            <ILobasArticlePageSummaryConfig>args.config,
            args.requestContext.apiSettings,
            articleId,
            args.requestContext
        );
    }
    throw new Error('Unable to create ArticlePageSummaryAction input, no articleId found in url');
};

const action = async (input: ArticlePageSummaryInput, context: IActionContext): Promise<IArticlePageSummary> => {
    const { config, articleId, apiSettings } = input;
    let article: IArticle | undefined;
    try {
        article = await getArticleByURLAsync(
            {
                callerContext: context,
                queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
            },
            articleId
        );
    } catch {
        // Do nothing, if there's an error we fall back to values defined from config
    }
    if (article) {
        const regex = /(<([^>]+)>)/gi;

        return {
            title: article.Title,
            description: article.Summary?.replace(regex, ''),
            sharingImageUrl: generateImageUrl(article.ThumbnailImage, apiSettings),
            canonicalUrl: context.requestContext.url.requestUrl.href,
            faviconUrl: config && config.faviconUrl,
            articleId: article.URL
        };

        // If the action fails fallback to values defined from config
    } else if (config) {
        return {
            title: config.title,
            description: config.description,
            sharingImageUrl: config.sharingImage && config.sharingImage.src,
            faviconUrl: config && config.faviconUrl
        };
    }
    return {};
};

export default createObservableDataAction({
    id: 'lobas-article-page-summary',
    action: <IAction<IArticlePageSummary>>action,
    input: createInput
});
